import React, { useState, useEffect } from 'react';
import { ChevronRight, DollarSign, Calendar, TrendingUp, ChevronDown } from 'lucide-react';
import { funnelService } from '../../../services/funnelService';

const FunnelVisualization = ({ funnel, leads, analytics, onLeadUpdate }) => {
  const [selectedLead, setSelectedLead] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);
  const [stageExpanded, setStageExpanded] = useState({});

  const stages = funnel?.funnel_stages?.sort((a, b) => a?.order_position - b?.order_position) || [];
  
  // Group leads by stage
  const leadsByStage = leads?.reduce((acc, lead) => {
    const stageId = lead?.current_stage_id;
    if (!acc?.[stageId]) {
      acc[stageId] = [];
    }
    acc?.[stageId]?.push(lead);
    return acc;
  }, {}) || {};

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStageId) => {
    e?.preventDefault();
    
    if (draggedLead && draggedLead?.current_stage_id !== targetStageId) {
      try {
        await funnelService?.moveLeadToStage(
          draggedLead?.id,
          draggedLead?.current_stage_id,
          targetStageId,
          funnel?.user_id,
          `Lead moved via drag and drop`
        );
        onLeadUpdate?.();
      } catch (error) {
        console.error('Error moving lead:', error);
      }
    }
    
    setDraggedLead(null);
  };

  const toggleStageExpansion = (stageId) => {
    setStageExpanded(prev => ({
      ...prev,
      [stageId]: !prev?.[stageId]
    }));
  };

  const getStageColor = (stageType) => {
    const colors = {
      discovery: 'bg-blue-100 border-blue-300 text-blue-800',
      qualification: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      proposal: 'bg-orange-100 border-orange-300 text-orange-800',
      negotiation: 'bg-purple-100 border-purple-300 text-purple-800',
      closing: 'bg-red-100 border-red-300 text-red-800',
      won: 'bg-green-100 border-green-300 text-green-800',
      lost: 'bg-gray-100 border-gray-300 text-gray-800'
    };
    return colors?.[stageType] || colors?.discovery;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  const LeadCard = ({ lead, isCompact = false }) => (
    <div
      draggable
      onDragStart={(e) => handleDragStart(e, lead)}
      onClick={() => setSelectedLead(lead)}
      className={`
        ${isCompact ? 'p-2' : 'p-3'} bg-white border border-gray-200 rounded-lg cursor-move hover:shadow-md transition-shadow
        ${draggedLead?.id === lead?.id ? 'opacity-50' : ''}
        ${selectedLead?.id === lead?.id ? 'ring-2 ring-blue-500' : ''}
      `}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium text-gray-900 truncate`}>
            {lead?.communication_contacts?.name || 'Unknown Contact'}
          </p>
          <p className={`${isCompact ? 'text-xs' : 'text-xs'} text-gray-500 truncate`}>
            {lead?.communication_contacts?.company || 'No Company'}
          </p>
        </div>
      </div>
      
      <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
        <span className="flex items-center">
          <DollarSign className="h-3 w-3 mr-1" />
          {formatCurrency(lead?.estimated_value)}
        </span>
        {lead?.probability && (
          <span className="text-green-600 font-medium">
            {Math.round(lead?.probability * 100)}%
          </span>
        )}
      </div>
      
      {lead?.next_action_date && (
        <div className="mt-1 flex items-center text-xs text-orange-600">
          <Calendar className="h-3 w-3 mr-1" />
          <span>Due {new Date(lead.next_action_date)?.toLocaleDateString()}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Funnel Overview */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{funnel?.name}</h3>
        <p className="text-sm text-gray-600 mb-3">{funnel?.description}</p>
        
        {/* Funnel Flow Visualization */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          {stages?.map((stage, index) => {
            const stageLeads = leadsByStage?.[stage?.id] || [];
            const stageValue = stageLeads?.reduce((sum, lead) => sum + (lead?.estimated_value || 0), 0);
            
            return (
              <React.Fragment key={stage?.id}>
                <div className="flex-shrink-0 min-w-32">
                  <div className={`p-3 rounded-lg border ${getStageColor(stage?.stage_type)}`}>
                    <div className="text-xs font-medium">{stage?.name}</div>
                    <div className="text-lg font-bold">{stageLeads?.length}</div>
                    <div className="text-xs">{formatCurrency(stageValue)}</div>
                  </div>
                </div>
                {index < stages?.length - 1 && (
                  <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {/* Interactive Funnel Board */}
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">Lead Pipeline</h4>
        
        <div className="grid gap-4 lg:grid-cols-3 xl:grid-cols-4">
          {stages?.map((stage) => {
            const stageLeads = leadsByStage?.[stage?.id] || [];
            const isExpanded = stageExpanded?.[stage?.id];
            const displayLeads = isExpanded ? stageLeads : stageLeads?.slice(0, 3);
            const hasMore = stageLeads?.length > 3;
            
            return (
              <div
                key={stage?.id}
                className="bg-gray-50 rounded-lg p-4"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage?.id)}
              >
                {/* Stage Header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h5 className="font-medium text-gray-900 text-sm">{stage?.name}</h5>
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      <span>{stageLeads?.length} leads</span>
                      {stage?.conversion_rate > 0 && (
                        <span className="flex items-center">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {stage?.conversion_rate}%
                        </span>
                      )}
                    </div>
                  </div>
                  {hasMore && (
                    <button
                      onClick={() => toggleStageExpansion(stage?.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          isExpanded ? 'transform rotate-180' : ''
                        }`}
                      />
                    </button>
                  )}
                </div>
                {/* Stage Leads */}
                <div className="space-y-2">
                  {displayLeads?.map((lead) => (
                    <LeadCard key={lead?.id} lead={lead} />
                  ))}
                  
                  {hasMore && !isExpanded && (
                    <button
                      onClick={() => toggleStageExpansion(stage?.id)}
                      className="w-full p-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      +{stageLeads?.length - 3} more leads
                    </button>
                  )}
                  
                  {stageLeads?.length === 0 && (
                    <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center">
                      <p className="text-xs text-gray-500">Drop leads here</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Selected Lead Details */}
      {selectedLead && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg bg-white rounded-md shadow-lg">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {selectedLead?.communication_contacts?.name || 'Lead Details'}
                </h3>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Company</label>
                  <p className="text-sm text-gray-900">{selectedLead?.communication_contacts?.company || 'N/A'}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Estimated Value</label>
                  <p className="text-sm text-gray-900">{formatCurrency(selectedLead?.estimated_value)}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Lead Source</label>
                  <p className="text-sm text-gray-900 capitalize">{selectedLead?.lead_source?.replace('_', ' ')}</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Lead Score</label>
                  <p className="text-sm text-gray-900">{selectedLead?.lead_score || 0}/100</p>
                </div>
                
                {selectedLead?.next_action && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Next Action</label>
                    <p className="text-sm text-gray-900">{selectedLead?.next_action}</p>
                  </div>
                )}
                
                {selectedLead?.expected_close_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Expected Close</label>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedLead.expected_close_date)?.toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FunnelVisualization;